import { EventBus } from './EventBus';
import { Modal } from './Modal';
import { mergeConfig, readConfigFromScript } from './Config';
import { buildIframeUrl } from '../utils/buildIframeUrl';
import type {
  FormaRentConfig,
  FormaRentEventMap,
  FormaRentEventName,
  OpenParams,
} from '../types';

/**
 * Публичный фасад виджета. Тонкий координатор:
 * - хранит config/modal/eventbus,
 * - открывает/закрывает модалку,
 * - пробрасывает подписки через EventBus.
 *
 * Фичи (hashLinks, dataButtons, analytics, prefill) получают инстанс и работают
 * через _config/_bus — это internal contract, не публичное API.
 */
export class FormaRent {
  private config: FormaRentConfig | null = null;
  private modal: Modal | null = null;
  private bus = new EventBus<FormaRentEventMap>();

  readonly version = __VERSION__;

  get isOpen(): boolean {
    return this.modal?.isOpen ?? false;
  }

  /** @internal — для features пакета, не публичный API. */
  get _config(): FormaRentConfig | null {
    return this.config;
  }

  /** @internal — для features пакета. */
  get _bus(): EventBus<FormaRentEventMap> {
    return this.bus;
  }

  /** @internal — для prefill-feature. Возвращает iframe или null если модалка не смонтирована. */
  _getIframe(): HTMLIFrameElement | null {
    return this.modal?.getIframe() ?? null;
  }

  /** @internal — регистр cleanup-функций от фич. Вызываются при destroy(). */
  private featureCleanups: Array<() => void> = [];

  /** @internal — фичи регистрируют свои cleanup-функции здесь при install. */
  _registerCleanup(fn: () => void): void {
    this.featureCleanups.push(fn);
  }

  init(partial: Partial<FormaRentConfig>): void {
    const fromScript = readConfigFromScript();
    this.config = mergeConfig(fromScript, partial);
  }

  open(params: OpenParams = {}): void {
    if (!this.config) {
      throw new Error('[FormaRent] Call init({user}) before open()');
    }
    const url = buildIframeUrl(this.config.baseUrl!, this.config, params);

    if (!this.modal) {
      this.modal = new Modal({
        iframeUrl: url,
        onClose: () => this.close(),
        onIframeReady: () => this.bus.emit('ready', undefined),
        onIframeError: () =>
          this.bus.emit('error', {
            code: 'IFRAME_LOAD_FAILED',
            message: 'Iframe did not load in time',
          }),
      });
      this.modal.mount();
    } else {
      this.modal.setUrl(url);
    }

    this.modal.open();
    this.bus.emit('open', params);
  }

  close(): void {
    if (!this.modal?.isOpen) return;
    this.modal.close();
    this.bus.emit('close', undefined);
  }

  on<K extends FormaRentEventName>(
    event: K,
    handler: (payload: FormaRentEventMap[K]) => void,
  ): () => void {
    return this.bus.on(event, handler);
  }

  off<K extends FormaRentEventName>(
    event: K,
    handler: (payload: FormaRentEventMap[K]) => void,
  ): void {
    this.bus.off(event, handler);
  }

  destroy(): void {
    for (const fn of this.featureCleanups) {
      try { fn(); } catch { /* best-effort */ }
    }
    this.featureCleanups = [];
    this.modal?.destroy();
    this.modal = null;
  }
}
